import { z } from 'zod';
import {
    CommanderFormProps,
    SchemaFormDefinition,
    SchemaFormFields,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { createZodFormResolver, getDefaults, lt, Validation } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { FILE_SCHEMA_OPTIONS, SUPPORTED_FILE_EXTENSIONS } from '../../../common/constants';
import { useGetServiceNamesByIdQuery } from '../../../common/queries/useGetServiceNamesByIdQuery';

const uploadFileSchema = z.object({
    file: Validation.File.Default(FILE_SCHEMA_OPTIONS),
    name: z.string().min(1).max(255),
    services: z.array(z.string()).optional(),
});

export type UploadFileFormValues = z.infer<typeof uploadFileSchema>;

const uploadFileFormDefinition = ({
    services,
}: {
    services: Record<string, string>;
}): SchemaFormDefinition<typeof uploadFileSchema> => ({
    fields: {
        file: {
            label: lt('File'),
            field: { type: 'file', accept: SUPPORTED_FILE_EXTENSIONS },
        },
        name: {
            label: lt('Name'),
        },
        services: {
            label: lt('ServicePlural'),
            field: {
                type: 'select',
                options: Object.entries(services).map(([value, label]) => createOption({ value, label })),
            },
            helper: lt(TextKey.ContractsAttachmentLibraryEditServicesTooltip),
        },
    },
});

export const UploadFileForm = (props: CommanderFormProps) => {
    const { data: services } = useGetServiceNamesByIdQuery();

    const { renderFormContent } = useHandledDialogFormControls<UploadFileFormValues>({
        ...props,
        defaultValues: getDefaults(uploadFileSchema),
        resolver: createZodFormResolver(uploadFileSchema),
        onSubmit: () => {},
    });

    return renderFormContent(
        <SchemaFormFields schema={uploadFileSchema} formDefinition={uploadFileFormDefinition({ services })} />
    );
};
